import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/client';
import type { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

/**
 * Read-only Hub finance for Nicole (rollup:NDS) and Wynand (rollup:WDS).
 * Not a rating. Hub GET /api/finance/metric returns budget+projection+actual
 * in one payload. Month = one proxy call; YTD = Portal sum of Ancora FY
 * months (Mar → selected month) via the same /finance/hub-metric endpoint.
 */

export type HubMetricPayload = {
  budget: number | null;
  projection: number | null;
  actual: number | null;
  variance: number | null;
  variancePct: number | null;
  currency?: 'ZAR' | string;
  asOf?: string | null;
  batchId?: string | null;
  hubMetricRef?: {
    personKey: string;
    source: string;
    entityKey: string;
    measure: string;
    period: string;
  };
  resolvedVia?: string;
  error?: string;
  code?: string;
  detail?: string;
};

const HUB_PERSON: Record<string, { personKey: string; entityKey: string; label: string }> = {
  nicole: { personKey: 'nicole', entityKey: 'rollup:NDS', label: 'Nicole · NDS' },
  wynand: { personKey: 'wynand', entityKey: 'rollup:WDS', label: 'Wynand · WDS' },
};

const MONTHS: Record<string, string> = {
  jan: '01', january: '01',
  feb: '02', february: '02',
  mar: '03', march: '03',
  apr: '04', april: '04',
  may: '05',
  jun: '06', june: '06',
  jul: '07', july: '07',
  aug: '08', august: '08',
  sep: '09', sept: '09', september: '09',
  oct: '10', october: '10',
  nov: '11', november: '11',
  dec: '12', december: '12',
};

/** Map period name / due date → Hub YYYY-MM. */
export function periodToHubMonth(periodName?: string | null, dueDate?: string | null): string | null {
  // Prefer explicit due-date filter so "All periods" does not pin an older rows[0] month.
  const due = (dueDate || '').trim();
  const dueMm = due.match(/^(\d{4})-(\d{2})(?:-\d{2})?/);
  if (dueMm) return `${dueMm[1]}-${dueMm[2]}`;
  const name = (periodName || '').trim();
  const yyyyMm = name.match(/^(\d{4})-(\d{2})$/);
  if (yyyyMm) return `${yyyyMm[1]}-${yyyyMm[2]}`;
  const named = name.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (named) {
    const mm = MONTHS[named[1].toLowerCase()];
    if (mm) return `${named[2]}-${mm}`;
  }
  return null;
}

/**
 * Ancora FY months from March through the selected YYYY-MM inclusive.
 * Jan/Feb → prior-year Mar–Dec plus Jan–Feb of the selected year.
 */
export function fyMonthsThrough(periodYm: string): string[] {
  const m = periodYm.match(/^(\d{4})-(\d{2})$/);
  if (!m) return [];
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return [];
  const startYear = month >= 3 ? year : year - 1;
  const out: string[] = [];
  let y = startYear;
  let mo = 3;
  while (y < year || (y === year && mo <= month)) {
    out.push(`${y}-${String(mo).padStart(2, '0')}`);
    mo += 1;
    if (mo > 12) {
      mo = 1;
      y += 1;
    }
  }
  return out;
}

export function hubPersonFromUpn(upn?: string | null) {
  if (!upn) return null;
  const local = upn.split('@')[0]?.toLowerCase().trim() || '';
  return HUB_PERSON[local] || null;
}

function formatZar(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function numOrZero(v: number | null | undefined): number {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return 0;
  return Number(v);
}

function computeVariance(data: Pick<HubMetricPayload, 'variance' | 'actual' | 'budget'> | undefined): number | null {
  if (!data) return null;
  if (data.variance != null) return data.variance;
  if (data.actual != null && data.budget != null) return Number(data.actual) - Number(data.budget);
  return null;
}

type Props = {
  employeeUpn: string;
  dueDate?: string;
  periodName?: string | null;
};

export default function HubFinanceTile({ employeeUpn, dueDate, periodName }: Props) {
  const api = useApi();
  const { getHubToken, requestHubConsent } = useAuth();
  const person = hubPersonFromUpn(employeeUpn);
  const period = periodToHubMonth(periodName, dueDate);
  const ytdYear = period ? period.slice(0, 4) : null;
  const fyMonths = period ? fyMonthsThrough(period) : [];

  const enabled = Boolean(person && period);

  const fetchMetric = async (periodKey: string): Promise<HubMetricPayload> => {
    const qs = new URLSearchParams({
      personKey: person!.personKey,
      source: 'team',
      entityKey: person!.entityKey,
      measure: 'actual',
      period: periodKey,
    });
    const hubAuth = await getHubToken();
    if (hubAuth.needsConsent) {
      const err = new Error('Hub finance consent required') as ApiError;
      err.status = 401;
      err.body = { code: 'HUB_CONSENT_REQUIRED', error: 'Hub finance consent required' };
      throw err;
    }
    if (!hubAuth.token) {
      const err = new Error('Hub finance token missing') as ApiError;
      err.status = 401;
      err.body = { code: 'HUB_AUTH_PENDING', error: 'Hub finance token missing' };
      throw err;
    }
    return api.get(`/finance/hub-metric?${qs.toString()}`, {
      'X-Hub-Authorization': `Bearer ${hubAuth.token}`,
    }) as Promise<HubMetricPayload>;
  };

  const monthQ = useQuery<HubMetricPayload, ApiError>({
    queryKey: ['hub-finance', person?.personKey, person?.entityKey, period],
    enabled,
    retry: false,
    queryFn: async () => fetchMetric(period!),
  });

  const ytdQ = useQuery<HubMetricPayload, ApiError>({
    queryKey: ['hub-finance-ytd', person?.personKey, person?.entityKey, period, fyMonths.join(',')],
    enabled: enabled && fyMonths.length > 0,
    retry: false,
    queryFn: async () => {
      // Acquire token once; reuse for all FY months.
      const hubAuth = await getHubToken();
      if (hubAuth.needsConsent) {
        const err = new Error('Hub finance consent required') as ApiError;
        err.status = 401;
        err.body = { code: 'HUB_CONSENT_REQUIRED', error: 'Hub finance consent required' };
        throw err;
      }
      if (!hubAuth.token) {
        const err = new Error('Hub finance token missing') as ApiError;
        err.status = 401;
        err.body = { code: 'HUB_AUTH_PENDING', error: 'Hub finance token missing' };
        throw err;
      }
      const hubHeaders = { 'X-Hub-Authorization': `Bearer ${hubAuth.token}` };

      const results = await Promise.all(
        fyMonths.map(async (ym) => {
          const qs = new URLSearchParams({
            personKey: person!.personKey,
            source: 'team',
            entityKey: person!.entityKey,
            measure: 'actual',
            period: ym,
          });
          try {
            const data = (await api.get(`/finance/hub-metric?${qs.toString()}`, hubHeaders)) as HubMetricPayload;
            if (data?.error || data?.code) return { ym, data: null as HubMetricPayload | null, status: 'empty' as const };
            return { ym, data, status: 'ok' as const };
          } catch (e) {
            const err = e as ApiError;
            const code = (err.body as HubMetricPayload | undefined)?.code;
            if (err.status === 401 || code === 'HUB_CONSENT_REQUIRED' || code === 'HUB_AUTH_PENDING') {
              throw err;
            }
            // 404 / NOT_FOUND / transient → treat month as zero contribution
            if (err.status === 404 || code === 'NOT_FOUND') {
              return { ym, data: null, status: 'empty' as const };
            }
            return { ym, data: null, status: 'error' as const };
          }
        }),
      );

      const hardErrors = results.filter((r) => r.status === 'error').length;
      const okCount = results.filter((r) => r.status === 'ok').length;
      if (okCount === 0 && hardErrors > 0) {
        const err = new Error('YTD unavailable') as ApiError;
        err.status = 502;
        err.body = { code: 'YTD_UNAVAILABLE', error: 'YTD unavailable' };
        throw err;
      }

      let budget = 0;
      let projection = 0;
      let actual = 0;
      for (const r of results) {
        if (!r.data) continue;
        budget += numOrZero(r.data.budget);
        projection += numOrZero(r.data.projection);
        actual += numOrZero(r.data.actual);
      }
      const variance = actual - budget;
      return {
        budget,
        projection,
        actual,
        variance,
        variancePct: budget !== 0 ? (variance / budget) * 100 : null,
        currency: 'ZAR',
        hubMetricRef: {
          personKey: person!.personKey,
          source: 'team',
          entityKey: person!.entityKey,
          measure: 'actual',
          period: `${fyMonths[0]}…${fyMonths[fyMonths.length - 1]}`,
        },
        resolvedVia: `Portal FY sum (${fyMonths.length} months)`,
      };
    },
  });

  if (!person) return null;

  const body = (monthQ.error as ApiError | undefined)?.body as HubMetricPayload | undefined;
  const code = body?.code || monthQ.data?.code;
  const errText = String(body?.error || monthQ.error?.message || '');
  const ytdBody = (ytdQ.error as ApiError | undefined)?.body as HubMetricPayload | undefined;
  const ytdCode = ytdBody?.code;

  const isConsent =
    code === 'HUB_CONSENT_REQUIRED' ||
    body?.code === 'HUB_CONSENT_REQUIRED' ||
    ytdCode === 'HUB_CONSENT_REQUIRED';
  const isAuthPending =
    !isConsent && (
      code === 'HUB_AUTH_PENDING' ||
      ytdCode === 'HUB_AUTH_PENDING' ||
      ((monthQ.error?.status === 502) && (body?.code === 'HUB_AUTH_PENDING' || /auth pending/i.test(errText)))
    );
  const isEmpty = monthQ.error?.status === 404 || code === 'NOT_FOUND';
  const monthData = monthQ.data && !monthQ.data.error ? monthQ.data : undefined;
  const ytdData = ytdQ.data && !ytdQ.data.error ? ytdQ.data : undefined;
  const ytdUnavailable =
    Boolean(ytdQ.error) &&
    !isConsent &&
    !isAuthPending &&
    (ytdCode === 'YTD_UNAVAILABLE' || ytdQ.isError);
  const monthVariance = computeVariance(monthData);
  const ytdVariance = computeVariance(ytdData);

  const headerPeriod =
    period && ytdYear
      ? ` · ${period} + YTD`
      : period
        ? ` · ${period}`
        : '';

  return (
    <div
      data-testid="hub-finance-tile"
      style={{
        margin: '0 0 1rem',
        padding: '12px 14px',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        background: '#f8fafc',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.04, color: '#334155', textTransform: 'uppercase' }}>
          Hub finance (read-only)
        </span>
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {person.label}
          {headerPeriod}
          {' · not a rating'}
        </span>
      </div>

      {!period && (
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>No period mapped (need YYYY-MM).</p>
      )}

      {enabled && (monthQ.isLoading || ytdQ.isLoading) && !monthData && (
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>Loading Hub finance…</p>
      )}

      {isConsent && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 6,
            color: '#1e40af',
            fontSize: 13,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span>Authorize Hub finance once (Finance.Metric.Read) to load these numbers.</span>
          <button
            type="button"
            onClick={() => { void requestHubConsent(); }}
            style={{
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 4,
              border: '1px solid #93c5fd',
              background: '#fff',
              color: '#1d4ed8',
              cursor: 'pointer',
            }}
          >
            Connect Hub finance
          </button>
        </div>
      )}

      {isAuthPending && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 6,
            color: '#92400e',
            fontSize: 13,
          }}
        >
          Hub finance token missing or rejected. Use Connect Hub finance, then refresh.
        </div>
      )}

      {isEmpty && !isAuthPending && !isConsent && (
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>No Hub finance for this period.</p>
      )}

      {monthQ.error && !isAuthPending && !isConsent && !isEmpty && (
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>
          Hub finance unavailable{body?.detail ? ` — ${typeof body.detail === 'string' ? body.detail : ''}` : '.'}
        </p>
      )}

      {monthData && (
        <MetricSection
          title={`Month ${period}`}
          data={monthData}
          variance={monthVariance}
        />
      )}

      {ytdData && (
        <MetricSection
          title={`YTD ${ytdYear} (FY Mar→${period?.slice(5)})`}
          data={ytdData}
          variance={ytdVariance}
        />
      )}

      {enabled && !ytdData && !isConsent && !isAuthPending && (ytdUnavailable || (ytdQ.isFetched && !ytdQ.isLoading && !ytdData)) && monthData && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#94a3b8' }}>YTD unavailable</p>
      )}

      {enabled && ytdQ.isLoading && monthData && !ytdData && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#94a3b8' }}>Loading YTD…</p>
      )}
    </div>
  );
}

function MetricSection({
  title,
  data,
  variance,
}: {
  title: string;
  data: HubMetricPayload;
  variance: number | null;
}) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>{title}</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 10,
        }}
      >
        <Metric label="Budget" value={formatZar(data.budget)} />
        <Metric label="Projection" value={formatZar(data.projection)} />
        <Metric label="Actual" value={formatZar(data.actual)} />
        <Metric label="Variance" value={formatZar(variance)} hint="actual − budget" />
      </div>
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: '#94a3b8' }}>{hint}</div>}
    </div>
  );
}
